<template>
  <div>
    <!-- do not turn on autocomplete for password fields, it is a security risk -->
    <text-field
      :id="id"
      v-model="form.password"
      :name="name"
      :label-name="labelName"
      :aria-label="ariaLabel"
      :type="passwordVisible ? 'text' : 'password'"
      :status="status"
      autocomplete="off"
      icon-static
      @blur="displayErrors"
    >
      <template #icon>
        <p id="password-text" aria-live="polite" class="sr-only">{{ passwordMessage }}</p>
        <button
          role="switch"
          class="password-field-icon"
          :aria-pressed="passwordVisible"
          aria-label="Show or hide password"
          aria-describedby="Password visibility toggle"
          type="button"
          @click="toggleVisibility"
        >
          <icon-show-password v-if="passwordVisible" />
          <icon-hide-password v-else />
        </button>
      </template>
    </text-field>
    <div v-if="allRequirementsMet" class="flex relative mt-4">
      <HelperText text="Nice work, your password is all set" :status="1" />
    </div>
    <div v-else class="mt-4">
      <ul v-for="(req, index) in requirements" :key="index" role="list" class="flex relative">
        <HelperText :text="generateHelperText(req)" :status="generateHelperTextStatus(req.validation, req.blur)" />
      </ul>
    </div>
  </div>
</template>

<script lang="ts">
import TextField from '../textfield/TextField.vue';
import IconHidePassword from '../../icons/IconHidePassword.vue';
import IconShowPassword from '../../icons/IconShowPassword.vue';
import helperStatus from '../../helpers/status';
import HelperText from '../helper_text/HelperText.vue';

const ariaLabel =
  'Create password: must be at least 8 characters long, contain at least one uppercase letter, lowercase letter, number, and special character.';

const requirements = [
  { helperText: '8 Characters minimum', blur: false, validation: /.{8,}/ },
  { helperText: '1 or more uppercase characters', blur: false, validation: /[A-Z]/ },
  { helperText: '1 or more lowercase characters', blur: false, validation: /[a-z]/ },
  { helperText: '1 or more numbers', blur: false, validation: /\d/ },
  { helperText: '1 or more special characters (!,$,%,@,*,&,^)', blur: false, validation: /\W/ },
];

export default {
  name: 'PasswordTextField',
  components: {
    TextField,
    IconHidePassword,
    IconShowPassword,
    HelperText,
  },
  model: {
    prop: 'modelValue',
    event: 'update:modelValue',
  },
  props: {
    id: {
      type: String,
      required: true,
      validator(value: String) {
        return !!value;
      },
    },
    modelValue: {
      type: String,
      default: '',
      required: false,
    },
    name: {
      type: String,
      default: '',
      required: false,
    },
    labelName: {
      type: String,
      required: true,
      validator(value: String) {
        return !!value;
      },
    },
    status: {
      type: Number as () => helperStatus,
      default: helperStatus.Neutral,
    },
  },
  data(): any {
    return {
      form: {
        password: this.modelValue || '',
      },
      passwordVisible: false,
      requirements,
      ariaLabel,
    };
  },
  computed: {
    passwordMessage(): string {
      return this.passwordVisible ? 'Password Visible' : 'Password Hidden';
    },
    allRequirementsMet(): boolean {
      return this.requirements.every((req) => req.validation.test(this.form.password));
    },
  },
  watch: {
    'form.password': function () {
      this.$emit('update:modelValue', this.form.password);
    },
    status(statusFromParent) {
      if (statusFromParent === helperStatus.Error) {
        this.displayErrors();
      }
    },
    modelValue(valueFromParent: String) {
      if (valueFromParent !== this.form.password) {
        this.form.password = valueFromParent;
      }
    },
  },
  methods: {
    toggleVisibility(e) {
      e.preventDefault();
      this.passwordVisible = !this.passwordVisible;
    },
    displayErrors() {
      this.$emit('blur');
      this.requirements.forEach((req) => {
        req.blur = true;
      });
    },
    generateHelperText(req) {
      if (this.generateHelperTextStatus(req.validation, req.blur) === helperStatus.Neutral) {
        return `<span class="mr-4">&#8226</span>${req.helperText}`;
      }
      return req.helperText;
    },
    generateHelperTextStatus(validator, blur) {
      const { password } = this.form;
      if (!validator.test(password) && blur) {
        return helperStatus.Error;
      }
      if (validator.test(password)) {
        return helperStatus.Valid;
      }
      return helperStatus.Neutral;
    },
  },
};
</script>

<style scoped lang="scss">
.password-field-icon {
  &:focus-visible {
    outline: 2px dotted theme('colors.grey.clickable');
    border-radius: 4px;
  }
}
</style>
